import { Auth } from 'aws-amplify';
import { useTranslation } from 'react-i18next';
import { useAuth } from './useAuth';
import { UserAddress } from '../stores/types/user.types';
import { validatePhoneNumber } from '../util/phone-validation';
import { PhoneNumberAlreadyExistsError } from '../errors/phone-number-already-exists';
import { CodeError } from '../constant/text.constant';

/**
 * Custom hook for managing user profile data and attributes in AWS Cognito.
 * Provides methods to update and verify user information including name, address,
 * phone number, email, language preferences, and driver terms acceptance.
 */
export const useUserProfile = () => {
  const auth = useAuth();
  const { i18n } = useTranslation();

  const ensureAuthenticated = () => {
    if (!auth.user || !auth.cognitoUser) {
      throw new Error('User is not authenticated');
    }
  };

  const updateUserLanguage = async (lang: string) => {
    ensureAuthenticated();

    const newProfile = {
      ...auth.user.attributes.profile,
      sitehostDashboardLanguage: lang,
    };

    await Auth.updateUserAttributes(auth.cognitoUser, {
      profile: JSON.stringify(newProfile),
    });

    i18n.changeLanguage(lang);
    // Fetch the latest user profile directly from Cognito
    await auth.refreshUserData();
  };

  const updateDriverTerms = async (version: string = '1.0') => {
    ensureAuthenticated();

    const newProfile = {
      ...auth.user.attributes.profile,
      acceptedDriverTerms: version,
    };

    await Auth.updateUserAttributes(auth.cognitoUser, {
      profile: JSON.stringify(newProfile),
    });

    await auth.refreshUserData();
  };

  /**
   * Checks if the user has accepted driver terms for the specified version (default version is '1.0').
   * Returns true if the accepted version matches or is greater than the required version.
   */
  const hasAcceptedDriverTerms = (version: string = '1.0'): boolean => {
    const acceptedVersion = auth.user?.attributes?.profile?.acceptedDriverTerms;
    if (!acceptedVersion) {
      return false;
    }
    return acceptedVersion === version || parseFloat(acceptedVersion) >= parseFloat(version);
  };

  const getPhoneNumber = (): string => {
    return auth.user.attributes?.phone_number || '';
  };

  const getUserLanguage = (): string | undefined => {
    return auth.user?.attributes?.profile?.sitehostDashboardLanguage;
  };

  const hasPhoneNumber = (): boolean => {
    return getPhoneNumber() !== '';
  };

  const isPhoneNumberVerified = (): boolean => {
    if (!auth.user?.attributes) {
      return false;
    }
    return Boolean(auth.user.attributes.phone_number_verified);
  };

  const hasVerifiedPhoneNumber = (): boolean => {
    return hasPhoneNumber() && isPhoneNumberVerified();
  };

  const updateUserName = async (givenName: string, familyName: string) => {
    ensureAuthenticated();
    const firstName = givenName.trim();
    const lastName = familyName.trim();
    const fullName = (firstName + ' ' + lastName).trim();

    await Auth.updateUserAttributes(auth.cognitoUser, {
      given_name: firstName,
      family_name: lastName,
      name: fullName,
    });
    await auth.refreshUserData();
  };

  const updateUserAddress = async (address: UserAddress) => {
    ensureAuthenticated();

    const newAddress = {
      addressLine1: address.addressLine1,
      addressLine2: address?.addressLine2 || '',
      city: address.city,
      province: address.province,
      country: address.country,
      postalCode: address.postalCode,
      isoCode: address.isoCode,
    };

    await Auth.updateUserAttributes(auth.cognitoUser, {
      address: JSON.stringify(newAddress),
    });
    await auth.refreshUserData();
  };

  /**
   * Updates the user's phone number. If the phone number is unchanged but unverified,
   * triggers verification. If already verified, skips the update.
   */
  const updateUserPhone = async (newPhone: string) => {
    ensureAuthenticated();

    const { normalized } = validatePhoneNumber(newPhone);
    const existingPhone = getPhoneNumber();

    if (existingPhone === normalized) {
      const existingPhoneVerified = isPhoneNumberVerified();
      if (existingPhoneVerified) {
        return;
      } else {
        await Auth.verifyCurrentUserAttribute('phone_number');
      }
    }

    try {
      await Auth.updateUserAttributes(auth.cognitoUser, {
        phone_number: normalized,
      });
    } catch (error: any) {
      throw new Error('Failed to update phone number: ' + error.message);
    }
  };

  /**
   * Verifies the phone number verification code. Throws PhoneNumberAlreadyExistsError
   * if the phone number is already associated with another account.
   */
  const verifyPhoneCode = async (code: string) => {
    ensureAuthenticated();
    await Auth.verifyCurrentUserAttributeSubmit('phone_number', code)
      .then(() => {
        auth.refreshUserData();
      })
      .catch((error: any) => {
        if (error.code === CodeError.COGNITO_ALIAS_ALREADY_EXISTS) {
          throw new PhoneNumberAlreadyExistsError();
        } else {
          throw new Error('Failed to verify phone code: ' + error.message);
        }
      });
  };

  const resendPhoneVerification = async () => {
    ensureAuthenticated();

    await Auth.verifyCurrentUserAttribute('phone_number');
  };

  const updateUserEmail = async (email: string) => {
    ensureAuthenticated();

    await Auth.updateUserAttributes(auth.cognitoUser, {
      email: email.trim(),
    });
    await auth.refreshUserData();
  };

  const verifyEmailCode = async (code: string) => {
    ensureAuthenticated();

    await Auth.verifyCurrentUserAttributeSubmit('email', code)
      .then(() => {
        auth.refreshUserData();
      })
      .catch((error) => {
        throw new Error('Failed to verify email code: ' + error.message);
      });
  };

  const resendEmailVerification = async () => {
    ensureAuthenticated();

    await Auth.verifyCurrentUserAttribute('email');
  };

  return {
    getUserLanguage,
    updateUserLanguage,
    updateDriverTerms,
    hasAcceptedDriverTerms,
    hasPhoneNumber,
    getPhoneNumber,
    isPhoneNumberVerified,
    hasVerifiedPhoneNumber,
    updateUserName,
    updateUserAddress,
    updateUserPhone,
    verifyPhoneCode,
    resendPhoneVerification,
    updateUserEmail,
    verifyEmailCode,
    resendEmailVerification,
  };
};
